<template>
  <div class="alv">
    <div v-if="!groups.length" class="empty-state">
      <div class="empty-state__icon">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round">
          <rect x="3.5" y="5" width="17" height="15.5" rx="2.5" /><path d="M3.5 10h17M8 3v4M16 3v4" />
        </svg>
      </div>
      <div class="empty-state__title">이 기간에 일정이 없습니다</div>
      <div class="empty-state__hint">우측 하단 추가 버튼으로 일정이나 업무를 만들 수 있습니다</div>
    </div>

    <section
      v-for="g in groups"
      :key="g.date"
      class="alv__group"
      :class="{ 'is-past': g.isPast }"
    >
      <header class="alv__date" :class="{ 'is-today': g.isToday }">
        <span class="alv__dlabel u-num">{{ g.dateLabel }}</span>
        <span class="alv__dow" :class="g.dayClass">({{ g.dow }})</span>
        <span v-if="g.isToday" class="tone-chip tone-chip--primary">오늘</span>
        <span v-if="g.holiday" class="alv__holiday">{{ g.holiday }}</span>
        <span class="alv__count count-badge u-num">{{ g.items.length }}</span>
      </header>

      <button
        v-for="it in g.items"
        :key="it.uid"
        type="button"
        class="alv__row"
        @click="$emit('open-item', it)"
      >
        <!--
          거터. 일정은 시각, 업무는 완료 표시가 **같은 44px 폭**을 차지한다.
          예전에는 시각이 제목 옆에 붙어 있어서 항목마다 제목 시작점이 달랐고,
          목록을 세로로 훑을 수가 없었다. AgendaDaySheet 와 같은 규칙이다.

          업무의 체크는 표시일 뿐 조작이 아니다 — 이 뷰의 부모(Calendar)는
          목록 뷰에서 open-item 만 듣는다. 눌러도 상태가 바뀌지 않을 컨트롤을
          체크박스처럼 보이게 두지 않으려고, 시트의 22px 사각형 대신
          작고 테두리가 얇은 상태 표식으로 그린다. 완료 토글은 일자 시트에 있다.
        -->
        <span v-if="it.kind === 'task'" class="alv__gutter alv__gutter--mark">
          <span class="alv__mark" :class="{ 'is-done': it.status === 'done' }" aria-hidden="true" />
        </span>
        <span v-else class="alv__gutter u-num">{{ it.allDay ? '종일' : timeLabel(it) || '—' }}</span>

        <span class="alv__bar" :style="{ background: it.color }" />

        <span class="alv__main">
          <span
            class="alv__name"
            :class="{ 'is-done': it.kind === 'task' && it.status === 'done' }"
          >{{ it.title }}</span>

          <span class="alv__sub">
            <span
              v-if="it.kind === 'schedule'"
              class="alv__type"
              :style="{ color: it.color }"
            >{{ it.typeLabel }}</span>
            <!-- 시각은 종류를 가리지 않는다. 업무 거터는 완료 표식이 차지했으므로
                 시각이 있는 업무(서버 composeTaskTimes 가 startTime/dueTime 중 하나라도
                 있으면 allDay:false 로 내려준다)는 여기 부제에서 시각을 보여 준다.
                 이 줄이 없으면 '14:00 회의 준비' 같은 업무가 목록 어디에도 시각이 없다. -->
            <span v-if="!it.allDay" class="u-num">{{ timeRangeLabel(it) }}</span>
            <span v-if="it.location" class="alv__loc">{{ it.location.name }}</span>
            <span v-if="it.kind === 'task' && it.department">{{ it.department.name }}</span>
            <span v-if="it.owner" class="alv__who">
              <el-avatar :size="16" :src="avatarOf(it.owner)">{{ initialOf(it.owner) }}</el-avatar>
              {{ it.owner.nickname }}
            </span>
            <span v-else-if="it.kind === 'task'" class="alv__unassigned">미배정</span>
          </span>
        </span>

        <!-- 우측 상태. 업무는 진행 상태, 일정은 반복 소속 여부다. -->
        <StatusPill v-if="it.kind === 'task'" class="alv__st" :status="it.status" />
        <span v-else-if="it.seriesId" class="alv__rep" role="img" aria-label="반복 일정">
          <svg aria-hidden="true" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M17 2l4 4-4 4" /><path d="M3 11V9a4 4 0 0 1 4-4h14" />
            <path d="M7 22l-4-4 4-4" /><path d="M21 13v2a4 4 0 0 1-4 4H3" />
          </svg>
        </span>
      </button>
    </section>
  </div>
</template>

<script>
import { computed } from 'vue'
import { useHolidays } from '@/composables/useHolidays'
import { ymd, timeLabel, timeRangeLabel } from '@/utils/agendaItem'
import { getProfileImageUrl, getProfileInitial } from '@/utils/profileUtils'
import StatusPill from '@/components/tasks/StatusPill.vue'

/**
 * 날짜별로 묶은 목록 뷰.
 *
 * 이 앱에 없던 뷰다. 모바일에서 정밀 탭이 필요 없고 엄지가 원하는 세로 플릭과 맞아서
 * 캘린더 뷰 중 가장 쓰기 편한 축에 든다. 다만 여러 날 걸친 일정(휴가/대직)이
 * 시작일 한 줄로만 보이므로, "이번 달에 누가 언제 빠지나"는 여전히 월 뷰가 낫다.
 * 그래서 기본값은 월이고 이건 선택지로 둔다.
 */
const WEEK = ['일', '월', '화', '수', '목', '금', '토']

export default {
  name: 'AgendaListView',
  components: { StatusPill },
  props: {
    items: { type: Array, default: () => [] },
    year: { type: Number, default: () => new Date().getFullYear() }
  },
  emits: ['open-item'],
  setup(props) {
    // 목록은 6주 그리드 범위라 연도 경계를 걸칠 수 있다(1월 목록의 12월 말).
    // 항목이 실제로 걸친 해를 모두 받는다.
    const holidayYears = computed(() => {
      const ys = new Set([props.year])
      for (const it of props.items) if (it.date) ys.add(Number(it.date.slice(0, 4)))
      return [...ys]
    })
    const { holidayName } = useHolidays(holidayYears)

    const groups = computed(() => {
      // '오늘' 은 목록을 다시 묶을 때마다 새로 잰다. 상수로 굳혀 두면 keep-alive 로 살아 있는
      // 캘린더에서 자정을 넘긴 뒤에도 어제가 '오늘' 로 남는다(돌아오면 목록은 다시 받는다).
      const todayYmd = ymd(new Date())
      const byDate = new Map()
      for (const it of props.items) {
        if (!it.date) continue
        if (!byDate.has(it.date)) byDate.set(it.date, [])
        byDate.get(it.date).push(it)
      }
      return [...byDate.entries()]
        .sort((a, b) => a[0].localeCompare(b[0]))
        .map(([date, list]) => {
          const d = new Date(`${date}T00:00:00`)
          const dow = d.getDay()
          return {
            date,
            isPast: date < todayYmd,
            isToday: date === todayYmd,
            // 헤더는 '8월 24일 (월)'. 큰 숫자 하나(24)만 두던 예전 헤더는
            // 월이 바뀌는 경계에서 무슨 날인지 알 수 없었다 — 목록은
            // 한 달 그리드를 넘겨서 보는 뷰라 월이 헤더에 있어야 한다.
            dateLabel: `${d.getMonth() + 1}월 ${d.getDate()}일`,
            dow: WEEK[dow],
            dayClass: dow === 0 ? 'is-sun' : dow === 6 ? 'is-sat' : '',
            holiday: holidayName(date),
            items: list.sort(
              (a, b) =>
                (a.allDay === b.allDay ? 0 : a.allDay ? -1 : 1) ||
                (a.startAt || '').localeCompare(b.startAt || '')
            )
          }
        })
    })

    return {
      groups, timeLabel, timeRangeLabel,
      avatarOf: (u) => getProfileImageUrl(u),
      initialOf: (u) => getProfileInitial(u)
    }
  }
}
</script>

<style scoped>
.alv {
  background: var(--color-surface);
  min-height: 100%;
  padding-bottom: var(--sp-6);
}

.alv__group { border-bottom: 1px solid var(--color-border); }

/* 지난 날. 지우지 않고 눌러 둔다 — "그날 뭐였더라" 를 확인하러 올라오는
   일이 잦고, 스크롤 위치가 오늘 근처에 있어야 그게 가능하다. */
.alv__group.is-past .alv__row { opacity: 0.55; }
.alv__group.is-past .alv__row:active { opacity: 1; }

/*
 * 날짜 헤더는 스티키다.
 *
 * top 이 0 이면 헤더가 고정 상단바(TopBar) 뒤로 들어가 보이지 않는다.
 * 페이지 자체가 window 스크롤이고, 위에는 TopBar(--topbar-h)와 캘린더
 * 컨트롤 바(.cal__bar, sticky)가 겹쳐 있다. 바 높이는 Calendar.vue 가
 * ResizeObserver 로 실측해 --cal-bar-h 로 내려준다(아래 참고).
 * z-index 는 컨트롤 바(--z-subbar)보다 낮게 둬서 그 아래로 지나가게 한다.
 */
.alv__date {
  display: flex;
  align-items: center;
  gap: var(--sp-2);
  padding: var(--sp-2) var(--sp-4);
  position: sticky;
  /* 바 높이는 Calendar.vue 가 ResizeObserver 로 실측해 --cal-bar-h 로 내려준다.
     상수를 적으면 바가 줄바꿈될 때(부서명이 길면 3줄까지 간다) 헤더가 바 뒤에 숨는다.
     폴백 61px = 패딩 8 + .cal__navbtn 44(--tap-min) + 8 + 테두리 1. */
  top: calc(var(--topbar-h) + var(--safe-top) + var(--cal-bar-h, 61px));
  background: var(--color-surface);
  border-bottom: 1px solid var(--color-border);
  z-index: 1;
}

.alv__dlabel {
  font-size: var(--fs-md);
  font-weight: var(--fw-semibold);
  color: var(--color-text);
}
.is-today .alv__dlabel { color: var(--color-primary); }

/* 요일 색은 캘린더 그리드와 같은 눌러 둔 쌍을 쓴다. 원색(--color-danger)을
   쓰면 헤더 줄이 목록 안의 실제 항목(휴가 빨강)보다 튄다. */
.alv__dow { font-size: var(--fs-sm); color: var(--color-text-muted); }
.alv__dow.is-sun { color: var(--cal-sun); }
.alv__dow.is-sat { color: var(--cal-sat); }
.alv__holiday { font-size: var(--fs-xs); color: var(--cal-sun); }

.alv__count { margin-left: auto; }

.alv__row {
  width: 100%;
  display: flex;
  align-items: stretch;
  gap: var(--sp-2);
  padding: var(--sp-2) var(--sp-4);
  min-height: var(--tap-comfortable);
  border: 0;
  border-bottom: 1px solid var(--color-border);
  background: none;
  text-align: left;
  cursor: pointer;
  transition: background var(--dur-fast) var(--ease);
}
.alv__row:last-child { border-bottom: 0; }
.alv__row:hover { background: var(--color-surface-2); }
.alv__row:active { background: var(--color-surface-2); }

/* 폭을 고정해야 제목들이 한 줄로 선다. 일정의 시각과 업무의 표식이
   같은 폭(--tap-min)을 차지한다. */
.alv__gutter {
  width: var(--tap-min);
  flex: 0 0 var(--tap-min);
  padding-top: 2px;
  padding-right: var(--sp-2);
  font-size: var(--fs-xs);
  color: var(--color-text-muted);
  text-align: right;
}
.alv__gutter--mark {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
}

/* 그려서 만드는 완료 표식. 완료는 초록 채움 + 흰 갈고리(::after).
   글리프(☑ ☐)를 쓰면 폰트마다 크기가 달라 제목 baseline 이 흔들린다. */
.alv__mark {
  width: 15px;
  height: 15px;
  border: 1.5px solid var(--color-border-strong);
  border-radius: var(--r-xs);
  position: relative;
  flex-shrink: 0;
}
.alv__mark.is-done {
  background: var(--color-success);
  border-color: var(--color-success);
}
.alv__mark.is-done::after {
  content: '';
  position: absolute;
  left: 4.5px;
  top: 1px;
  width: 3px;
  height: 8px;
  border: solid var(--color-surface);
  border-width: 0 1.8px 1.8px 0;
  transform: rotate(42deg);
}

.alv__bar { width: 3px; border-radius: var(--r-full); flex-shrink: 0; }

.alv__main {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.alv__name {
  font-size: var(--fs-md);
  font-weight: var(--fw-medium);
  color: var(--color-text);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
/* 완료 업무. --color-text-subtle 에 취소선까지 겹치면 모바일에서 읽히지
   않는다(TaskCard 와 같은 판단) — 한 단계 진한 muted 로 둔다. */
.alv__name.is-done { color: var(--color-text-muted); text-decoration: line-through; }

.alv__sub {
  display: flex;
  align-items: center;
  gap: var(--sp-2);
  font-size: var(--fs-xs);
  color: var(--color-text-muted);
  overflow: hidden;
  white-space: nowrap;
}

.alv__type { font-weight: var(--fw-semibold); flex-shrink: 0; }
.alv__loc { overflow: hidden; text-overflow: ellipsis; }
.alv__who { display: inline-flex; align-items: center; gap: 4px; flex-shrink: 0; }
.alv__who :deep(.el-avatar) { border-width: 1px; box-shadow: none; font-size: var(--fs-3xs); }
.alv__unassigned { color: var(--color-warning); flex-shrink: 0; }

/* 우측 상태 필은 <StatusPill> 이다. 예전에는 여기서 --task-*-bg/fg 토큰 쌍을
   손으로 다시 적었는데, 그러면 상태 색 규칙이 두 곳에 살아서 한쪽만 바뀐다.
   같은 자리를 AgendaDaySheet 도 <StatusPill> 로 그리므로 이제 둘이 같다.
   여기서는 행 배치에 필요한 것만 얹는다. */
.alv__st {
  align-self: center;
  flex-shrink: 0;
}

.alv__rep {
  align-self: center;
  flex-shrink: 0;
  display: inline-flex;
  color: var(--color-text-subtle);
}

@media (max-width: 767px) { /* --bp-mobile-max */
  /* 모바일 컨트롤 바는 기간 제목이 첫 줄로 빠져 두 줄(약 88px)이 된다 */
  /* 모바일 폴백 91px = 8 + 기간 줄 26 + row-gap 4 + 버튼 줄 44 + 8 + 1.
     실제 값은 데스크톱과 같은 --cal-bar-h 로 들어온다(줄 수가 달라도 맞는다). */
  .alv__date { top: calc(var(--topbar-h) + var(--safe-top) + var(--cal-bar-h, 91px)); }
  .alv__row { padding: var(--sp-2) var(--sp-3); }
  .alv__date { padding: var(--sp-2) var(--sp-3); }
}
</style>
